import React from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import renderIcon from "../../../components/renderIcon";
import { applyLang, getCurrencyFormater } from "../../../i18n";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { sharedStyles, SectionHead } from "./_shared";
import { resolveImage } from "../utils";
import { readMainCaisseLabel } from "../compat";

const styles = `
.t26-caisses__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(16px, 2.5vw, 24px);
}
.t26-caisse {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  display: flex; flex-direction: column;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.t26-caisse:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
  border-color: color-mix(in oklab, var(--primary) 35%, var(--line));
}

/* Media : carrousel si imagesCaroussel, sinon image fixe, sinon placeholder visuel. */
.t26-caisse__media {
  aspect-ratio: 16 / 10;
  background: color-mix(in oklab, var(--secondary) 14%, var(--bg-2));
  position: relative;
  overflow: hidden;
}
.t26-caisse__media img { width: 100%; height: 100%; object-fit: cover; }

/* Slides du carrousel : cross-fade auto toutes les 4s */
.t26-caisse__slide {
  position: absolute; inset: 0;
  opacity: 0;
  transition: opacity .8s ease;
}
.t26-caisse__slide.is-active { opacity: 1; }
.t26-caisse__slide img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
}
/* Dots du carrousel en bas */
.t26-caisse__dots {
  position: absolute; z-index: 2;
  bottom: 10px;
  inset-inline-start: 0; inset-inline-end: 0;
  display: flex; gap: 5px; justify-content: center;
}
.t26-caisse__dot {
  width: 18px; height: 3px;
  border-radius: 2px;
  background: rgba(255,255,255,.45);
  border: 0; padding: 0; cursor: pointer;
  transition: width .25s ease, background .25s ease;
}
.t26-caisse__dot.is-active { width: 28px; background: #fff; }

.t26-caisse__media--placeholder {
  display: flex; align-items: center; justify-content: center;
  background:
    radial-gradient(60% 70% at 30% 30%, color-mix(in oklab, var(--secondary) 30%, transparent) 0%, transparent 70%),
    linear-gradient(135deg,
      color-mix(in oklab, var(--primary) 14%, var(--surface)) 0%,
      color-mix(in oklab, var(--secondary) 12%, var(--surface)) 100%);
}
.t26-caisse__media--placeholder svg {
  color: color-mix(in oklab, var(--primary) 70%, transparent);
  opacity: .55;
}

.t26-caisse__body {
  padding: 18px;
  display: flex; flex-direction: column; gap: 12px;
  flex: 1;
}
.t26-caisse__title {
  font-family: var(--font-display);
  font-size: 18px; font-weight: 700; line-height: 1.25;
  margin: 0; color: var(--ink);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.t26-caisse__progress {
  display: flex; flex-direction: column; gap: 6px;
}
.t26-caisse__bar {
  height: 8px;
  background: color-mix(in oklab, var(--primary) 10%, var(--bg-2));
  border-radius: var(--r-pill);
  overflow: hidden;
}
.t26-caisse__bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%);
  border-radius: var(--r-pill);
  transition: width .8s ease;
}
.t26-caisse__stats {
  display: flex; justify-content: space-between;
  font-size: 12px; color: var(--ink-3);
}
.t26-caisse__stats b { color: var(--ink); font-weight: 700; font-size: 14px; }
.t26-caisse__cta {
  margin-top: auto;
  padding: 10px 16px;
  background: var(--primary);
  color: var(--primary-contrast);
  border-radius: var(--r-pill);
  font-size: 13px; font-weight: 600;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  text-decoration: none;
  transition: transform .15s ease;
}
.t26-caisse__cta:hover { transform: translateY(-1px); }
`;

// Choisit la source media de la carte selon ce qui est dispo :
//   1. imagesCaroussel (tableau ≥ 2 images) → carrousel auto-rotate
//   2. imagesCaroussel (1 seule image) → image fixe
//   3. caisse.image (champ unique) → image fixe
//   4. caisse.bgimage (image de fond de la page Don) → image fixe en fallback
//   5. caisse.metaImage (image SEO / partage) → image fixe en dernier fallback
//   6. rien → placeholder gradient + icône
function CaisseMedia({ caisse }) {
    const carousel = Array.isArray(caisse.imagesCaroussel)
        ? caisse.imagesCaroussel.filter(Boolean)
        : [];
    // bgimage = image de fond utilisée sur la page Don (hero overlay).
    // metaImage = image SEO/partage de la cagnotte ; sert d'ultime fallback
    // visuel pour la card si aucune image dédiée n'est définie.
    const fixedImage = caisse.image || caisse.bgimage || caisse.metaImage || null;

    const [idx, setIdx] = React.useState(0);

    React.useEffect(() => {
        if (carousel.length <= 1) return;
        const t = setInterval(() => {
            setIdx(i => (i + 1) % carousel.length);
        }, 4000);
        return () => clearInterval(t);
    }, [carousel.length]);

    if (carousel.length >= 2) {
        return (
            <div className="t26-caisse__media">
                {carousel.map((src, i) => (
                    <div key={i} className={`t26-caisse__slide${i === idx ? " is-active" : ""}`} aria-hidden={i !== idx}>
                        <img src={resolveImage(src)} alt={caisse.name || ""} loading="lazy" />
                    </div>
                ))}
                <div className="t26-caisse__dots">
                    {carousel.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            className={`t26-caisse__dot${i === idx ? " is-active" : ""}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
                            aria-label={`Image ${i + 1}`}
                        />
                    ))}
                </div>
            </div>
        );
    }

    const single = carousel[0] || fixedImage;
    if (single) {
        return (
            <div className="t26-caisse__media">
                <img src={resolveImage(single)} alt={caisse.name || ""} loading="lazy" />
            </div>
        );
    }

    return (
        <div className="t26-caisse__media t26-caisse__media--placeholder">
            {renderIcon({ name: "IconHeartHandshake", source: "tabler" }, 56)}
        </div>
    );
}

export default function CaissesPreview({ context, limit = 4, eyebrow, title, subtitle }) {
    const { org, config, lang } = context;
    const currencyFormater = getCurrencyFormater(context);
    if (config["home.sections.disable.don"] === true) return null;
    if (!Array.isArray(org?.caisses)) return null;

    // On garde les caisses actives non masquées non pausées.
    // Les caisses avec objectif atteint sont exclues. Celles sans objectif sont
    // affichées (mais sans barre de progression).
    // Tri : du plus récent au plus ancien (createdAt/datetime décroissant ;
    // fallback sur l'ordre inverse du tableau API si pas de date).
    const filtered = org.caisses
        .filter(c => c && !c.paused && !c.hidden)
        .filter(c => !c.withObjectif || c.total < c.objectif);

    const dateOf = (c) => {
        const d = c.createdAt || c.datetime || c.date;
        const ms = d ? Date.parse(d) : NaN;
        return isNaN(ms) ? null : ms;
    };

    const sorted = [...filtered].sort((a, b) => {
        const da = dateOf(a);
        const db = dateOf(b);
        if (da != null && db != null) return db - da;            // récent → ancien
        if (da != null) return -1;
        if (db != null) return 1;
        // Pas de date : on inverse l'ordre du tableau (dernier inséré = en premier)
        return filtered.indexOf(b) - filtered.indexOf(a);
    });

    const caisses = sorted.slice(0, limit).map(c => applyLang(c, lang));

    if (caisses.length === 0) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + styles }} />
            <section
                className="t26-section"
                style={{ background: "color-mix(in oklab, var(--bg-2) 50%, transparent)", maxWidth: "none" }}
            >
                <div style={{ maxWidth: "var(--container)", margin: "0 auto" }}>
                    {/* Legacy : don.main.label Ummah (ex: "Don Spontané") override l'eyebrow */}
                    <SectionHead
                        eyebrow={eyebrow || readMainCaisseLabel(config) || t`Soutenir`}
                        title={title || t`Participez à nos causes`}
                        subtitle={subtitle || t`Chaque don compte. Ensemble, faisons avancer nos projets`}
                    />

                    <div className="t26-caisses__grid">
                        {caisses.map(c => {
                            const url = `${org.BASE_WEB_URL}/don/${c.id}`;
                            const hasObjectif = !!c.withObjectif && c.objectif > 0;
                            const ratio = hasObjectif
                                ? Math.min(100, (c.total / c.objectif) * 100)
                                : 0;

                            return (
                                <div key={c._id || c.id} className="t26-caisse">
                                    <CaisseMedia caisse={c} />
                                    <div className="t26-caisse__body">
                                        <h3 className="t26-caisse__title">{c.name}</h3>

                                        {hasObjectif && (
                                            <div className="t26-caisse__progress">
                                                <div className="t26-caisse__bar">
                                                    <div className="t26-caisse__bar-fill" style={{ width: `${ratio}%` }} />
                                                </div>
                                                <div className="t26-caisse__stats">
                                                    <span><b>{currencyFormater(c.total)}</b> <Trans>collectés</Trans></span>
                                                    <span><Trans>sur</Trans> <b>{currencyFormater(c.objectif)}</b></span>
                                                </div>
                                            </div>
                                        )}

                                        <Link href={url} legacyBehavior>
                                            <a className="t26-caisse__cta">
                                                <Icon name="heart" size={14} />
                                                <Trans>Soutenir</Trans>
                                            </a>
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
}
